import type { Message } from '@/features/messages';
import { MessageLayout } from '@/features/messages/components/message-layout/message-layout';
import { cn } from '@/shared/utilities';
import { Typography } from '@mui/material';
import type { ReactNode } from 'react';

interface MessageListItemTextProps {
  message: Message;
  isCurUser?: boolean;
  mode?: 'compact' | 'comfortable';
  isLast?: boolean;
}

export function MessageListItemText({ message, isCurUser = false, mode, isLast }: MessageListItemTextProps) {
  const renderContentWithLinks = (text: string): ReactNode[] => {
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    const nodes: ReactNode[] = [];
    let lastIndex = 0;
    let execResult: RegExpExecArray | null = urlRegex.exec(text);

    // Build nodes preserving order and using stable position-based keys
    while (execResult !== null) {
      const match = execResult;

      if (match.index > lastIndex) {
        nodes.push(text.slice(lastIndex, match.index));
      }

      const urlText = match[0];
      const hasProtocol = /^https?:\/\//i.test(urlText);
      const href = hasProtocol ? urlText : `http://${urlText}`;

      nodes.push(
        <a
          key={`link-${match.index}`}
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          className='mex:break-words mex:text-primary mex:hover:underline'
        >
          {urlText}
        </a>
      );

      lastIndex = urlRegex.lastIndex;
      execResult = urlRegex.exec(text);
    }

    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex));
    }

    return nodes;
  };

  return (
    <MessageLayout
      isOwn={isCurUser}
      senderId={message.senderId}
      sentAt={message.sentAt}
      message={message}
      mode={mode}
      isLast={isLast}
    >
      <Typography
        variant='inherit'
        className={cn('mex:!m-2 mex:text-[15px]', message.isRevoked && 'mex:!italic mex:text-text-disabled')}
      >
        {renderContentWithLinks(message.content)}
      </Typography>
    </MessageLayout>
  );
}
